import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FiLock, FiShield, FiInfo } from "react-icons/fi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CardSkeleton } from "@/components/common";
import { SEO } from "@/components/seo";
import {
  FormProgress,
  ApplicantsSection,
  EmploymentSection,
  AssetsSection,
  LiabilitiesSection,
  IncomeSection,
  ExpensesSection,
  RequirementsSection,
  NextStepsSection,
} from "./_components/fact-finder";
import type {
  FactFinderSection,
  ApplicantData,
  EmploymentData,
  AssetsData,
  LiabilitiesData,
  IncomeData,
  ExpensesData,
  RequirementsData,
} from "@/types/fact-finder";
import { FactFinderConfigProvider, useFactFinderConfig } from "@/contexts/FactFinderConfigContext";
import { getActiveSections } from "@/config/fact-finder";

interface FactFinderData {
  id: string;
  status: string;
  completedSections: string[];
  lastSectionSaved: string | null;
  applicant1: ApplicantData | null;
  applicant2: ApplicantData | null;
  employment: EmploymentData | null;
  assets: AssetsData | null;
  liabilities: LiabilitiesData | null;
  income: IncomeData | null;
  expenses: ExpensesData | null;
  requirements: RequirementsData | null;
}

function FactFinderPageContent() {
  const navigate = useNavigate();
  const config = useFactFinderConfig();
  const [factFinder, setFactFinder] = useState<FactFinderData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSection, setCurrentSection] = useState<FactFinderSection>("applicants");

  // Get sections based on mode
  const activeSections = getActiveSections(config) as readonly FactFinderSection[];

  const fetchOrCreateFactFinder = useCallback(async () => {
    try {
      // First try to get existing
      const getResponse = await fetch("/api/fact-finder", {
        credentials: "include",
      });

      if (getResponse.ok) {
        const data = await getResponse.json() as FactFinderData | null;
        if (data) {
          setFactFinder(data);
          // Set current section to last saved, but only if it's in active sections
          if (data.lastSectionSaved && activeSections.includes(data.lastSectionSaved as FactFinderSection)) {
            setCurrentSection(data.lastSectionSaved as FactFinderSection);
          } else {
            // Default to first section
            setCurrentSection(activeSections[0]);
          }
        } else {
          // Create new
          const createResponse = await fetch("/api/fact-finder", {
            method: "POST",
            credentials: "include",
          });
          if (createResponse.ok) {
            const newData = await createResponse.json() as FactFinderData;
            setFactFinder(newData);
          }
        }
      }
    } catch (error) {
      console.error("Failed to load fact finder:", error);
      toast.error("Failed to load form");
    } finally {
      setIsLoading(false);
    }
  }, [activeSections]);

  useEffect(() => {
    fetchOrCreateFactFinder();
  }, [fetchOrCreateFactFinder]);

  const saveSection = async (section: FactFinderSection, data: unknown) => {
    if (!factFinder) return;

    try {
      const response = await fetch(`/api/fact-finders/${factFinder.id}/section/${section}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data }),
      });

      if (response.ok) {
        // Update local state
        setFactFinder((prev) => {
          if (!prev) return prev;
          const completedSections = prev.completedSections.includes(section)
            ? prev.completedSections
            : [...prev.completedSections, section];
          return {
            ...prev,
            [section]: data,
            completedSections,
            lastSectionSaved: section,
          };
        });
        toast.success("Progress saved");
      } else {
        toast.error("Failed to save");
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  const handleSubmit = async () => {
    if (!factFinder) return;

    try {
      const response = await fetch(`/api/fact-finder/${factFinder.id}/submit`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Fact finder submitted successfully!");
        navigate("/portal");
      } else {
        toast.error("Failed to submit");
      }
    } catch {
      toast.error("Failed to submit");
    }
  };

  const goToNextSection = () => {
    const currentIndex = activeSections.indexOf(currentSection);
    if (currentIndex < activeSections.length - 1) {
      setCurrentSection(activeSections[currentIndex + 1]);
    }
  };

  const saveApplicants = async (applicant1: ApplicantData, applicant2: ApplicantData | null) => {
    if (!factFinder) return;

    try {
      // Save applicant1
      const response1 = await fetch(`/api/fact-finders/${factFinder.id}/section/applicant1`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: applicant1 }),
      });

      // Save applicant2 if present
      if (applicant2) {
        await fetch(`/api/fact-finders/${factFinder.id}/section/applicant2`, {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ data: applicant2 }),
        });
      }

      if (response1.ok) {
        setFactFinder((prev) => {
          if (!prev) return prev;
          const completedSections = prev.completedSections.includes("applicants")
            ? prev.completedSections
            : [...prev.completedSections, "applicants"];
          return {
            ...prev,
            applicant1,
            applicant2,
            completedSections,
            lastSectionSaved: "applicants",
          };
        });
        toast.success("Progress saved");
      } else {
        toast.error("Failed to save");
      }
    } catch {
      toast.error("Failed to save");
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <CardSkeleton />
          </div>
          <div className="lg:col-span-3">
            <CardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  if (!factFinder) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Unable to Load Form</h1>
        <p className="text-muted-foreground mb-6">
          Please try refreshing the page or contact support.
        </p>
        <Button onClick={() => window.location.reload()}>Refresh Page</Button>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Fact Finder Form"
        description="Complete your financial information form securely"
        noIndex
      />

      <div className="bg-cream min-h-screen">
        {/* Header */}
        <div className="bg-primary text-primary-foreground py-8">
          <div className="max-w-6xl mx-auto px-4">
            <h1 className="text-3xl font-serif">Fact Finder</h1>
            <p className="text-primary-foreground/80 mt-2">
              Complete this form to help us find the best loan options for you
            </p>
            <div className="flex items-center gap-4 mt-4 text-sm text-primary-foreground/90">
              <div className="flex items-center gap-2">
                <FiLock className="size-4" />
                <span>256-bit encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <FiShield className="size-4" />
                <span>Your data is secure</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-6xl mx-auto px-4 py-8">
          {/* Info Banner */}
          <Card className="mb-8 bg-blue-50 border-blue-200">
            <CardContent className="flex items-start gap-4 p-4">
              <FiInfo className="size-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Your progress is saved automatically</p>
                <p className="mt-1">
                  You can leave and return at any time. All information is encrypted and stored securely.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-4 gap-8">
            {/* Progress Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <FormProgress
                  currentSection={currentSection}
                  completedSections={factFinder.completedSections}
                  onSectionClick={setCurrentSection}
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="lg:col-span-3">
              <Card>
                <CardContent className="p-6 md:p-8">
                  {currentSection === "applicants" && (
                    <ApplicantsSection
                      applicant1={factFinder.applicant1}
                      applicant2={factFinder.applicant2}
                      onSave={saveApplicants}
                      onNext={goToNextSection}
                    />
                  )}

                  {/* Advanced mode sections */}
                  {config.advancedMode && currentSection === "employment" && (
                    <EmploymentSection
                      data={factFinder.employment}
                      hasApplicant2={!!factFinder.applicant2}
                      onSave={(data) => saveSection("employment", data)}
                      onNext={goToNextSection}
                    />
                  )}

                  {config.advancedMode && currentSection === "assets" && (
                    <AssetsSection
                      data={factFinder.assets}
                      hasApplicant2={!!factFinder.applicant2}
                      onSave={(data) => saveSection("assets", data)}
                      onNext={goToNextSection}
                    />
                  )}

                  {config.advancedMode && currentSection === "liabilities" && (
                    <LiabilitiesSection
                      data={factFinder.liabilities}
                      hasApplicant2={!!factFinder.applicant2}
                      onSave={(data) => saveSection("liabilities", data)}
                      onNext={goToNextSection}
                    />
                  )}

                  {config.advancedMode && currentSection === "income" && (
                    <IncomeSection
                      data={factFinder.income}
                      hasApplicant2={!!factFinder.applicant2}
                      onSave={(data) => saveSection("income", data)}
                      onNext={goToNextSection}
                    />
                  )}

                  {config.advancedMode && currentSection === "expenses" && (
                    <ExpensesSection
                      data={factFinder.expenses}
                      onSave={(data) => saveSection("expenses", data)}
                      onNext={goToNextSection}
                    />
                  )}

                  {config.advancedMode && currentSection === "requirements" && (
                    <RequirementsSection
                      data={factFinder.requirements}
                      onSave={(data) => saveSection("requirements", data)}
                      onNext={goToNextSection}
                    />
                  )}

                  {/* Next Steps - Basic mode only */}
                  {!config.advancedMode && currentSection === "next-steps" && (
                    <NextStepsSection
                      onSave={() => saveSection("next-steps", { acknowledged: true })}
                      onNext={goToNextSection}
                    />
                  )}

                  {currentSection === "consent" && (
                    <ConsentSection onSubmit={handleSubmit} />
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ConsentSection({ onSubmit }: { onSubmit: () => void }) {
  const [agreed, setAgreed] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-serif">Privacy & Consent</h2>
        <p className="text-muted-foreground mt-2">
          Please review and agree to the terms below to submit your application.
        </p>
      </div>

      <div className="bg-muted p-6 rounded-lg max-h-64 overflow-y-auto text-sm space-y-4">
        <h3 className="font-medium">Privacy Consent</h3>
        <p>
          By signing below, you agree that we may collect, use and disclose your
          information as specified in our Privacy Policy.
        </p>
        <p>
          The information you provide will be used to arrange or provide finance
          and other services. We may exchange the information with providers of
          finance and other products, financial consultants, lawyers, accountants,
          and other professionals.
        </p>
        <p>
          You may gain access to the personal information that we hold about you
          by contacting us. Our Privacy Policy contains information about how you
          may access or seek correction of the information we hold about you.
        </p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={agreed}
          onChange={(e) => setAgreed(e.target.checked)}
          className="w-5 h-5 rounded border-gray-300 mt-0.5"
        />
        <span className="text-sm">
          I declare that the information provided is true and correct, and I consent
          to the collection, use, and disclosure of my information as described above.
        </span>
      </label>

      <div className="flex justify-end pt-4 border-t">
        <Button onClick={onSubmit} disabled={!agreed} size="lg">
          Submit Fact Finder
        </Button>
      </div>
    </div>
  );
}

/**
 * Main export - wraps the page content with the config provider
 */
export function FactFinderPage() {
  return (
    <FactFinderConfigProvider>
      <FactFinderPageContent />
    </FactFinderConfigProvider>
  );
}
